import {
  Room,
  RoomEvent,
  Track,
  ExternalE2EEKeyProvider,
  isE2EESupported,
  setLogLevel,
  LogLevel,
  type RemoteTrack,
  type RemoteTrackPublication,
  type RemoteParticipant,
  type TrackPublication,
  type Participant,
} from "livekit-client";
import { decodeIncoming, type DcEnvelope, type PublishFn } from "@diy-avatar/shared";
import { E2EE_KEY_PROVIDER_OPTS } from "@diy-avatar/shared/e2ee";
import {
  classifyConnectError,
  isIceFailureDisconnectReason,
  type ConnectErrorInfo,
} from "@diy-avatar/shared/connect_error";
import { logDiagFailure } from "@diy-avatar/shared/log_shipper_browser";
import { mediaKeyToArrayBuffer } from "./e2ee";
import {
  getMediaKeys,
  DEVICE_KEY_SCOPE,
  DEVICE_KEY_SCOPE_ID,
} from "./e2eeSession";

// Lower livekit-client's default log verbosity at module load. Without a media
// key the e2ee worker logs "No key set found" on EVERY frame (tens per second),
// drowning the console forever. The EncryptionError RoomEvent (see ctor) is the
// structured signal the UI reacts to instead; the per-frame spam carries no
// extra info. `warn` keeps genuine warnings/errors while muting the flood.
setLogLevel(LogLevel.warn);

export type ConnectionState =
  | "idle"
  | "connecting"
  | "connected"
  | "disconnected"
  | "error";

export interface SubscriberHandlers {
  onState: (s: ConnectionState) => void;
  onTrack: (
    track: RemoteTrack,
    publication: RemoteTrackPublication,
    participant: RemoteParticipant,
  ) => void;
  onTrackUnsubscribed?: (track: RemoteTrack) => void;
  /**
   * Connect/join failed. `info` is the classified, non-localized diagnosis
   * (category + stable code + i18n key + raw detail) so the UI can render a
   * SPECIFIC, actionable message instead of LiveKit's internal string. The raw
   * `err` is still passed for callers that need the original object.
   */
  onError?: (err: unknown, info?: ConnectErrorInfo) => void;
  onData?: (env: DcEnvelope) => void;
  /**
   * Fired when the e2ee worker reports a decryption/encryption error (e.g. a
   * missing key at index 0 because no media key has been installed yet). Lets
   * the UI drive a "key missing / repair" state instead of relying on the
   * silent 30s late-key retry while the worker logs "No key set found" per
   * frame with no other signal.
   */
  onEncryptionError?: (err: unknown) => void;
  /**
   * Fired when the DEVICE participant joins (true) or leaves (false) the room.
   * Pause is ephemeral data-channel signaling with no resync: if the teacher
   * kiosk restarts while paused, it republishes media but never sends `resume`,
   * leaving the student stuck on the Wait overlay. A device DISCONNECT is the
   * authoritative "publisher is gone" signal that lets the UI clear that stale
   * paused state. A transient ICE reconnect does NOT fire ParticipantDisconnected
   * (the remote participant stays), so a genuine pause does not flicker.
   */
  onDevicePresence?: (present: boolean) => void;
  /**
   * Fired whenever the device participant's set of subscribed VIDEO
   * publications changes (subscribe/unsubscribe). Lets the UI recompute the
   * camera dropdown list. Audio handling is unaffected.
   */
  onCameraTracksChanged?: () => void;
  /**
   * Fired when the set of CO-VIEWING student participants changes (another
   * student joins/leaves the room). The handler receives the current list of
   * non-device identities so the UI can render the viewer-avatar strip. Fires
   * on connect, on participant connect/disconnect, and on reconnect.
   */
  onViewersChanged?: (identities: string[]) => void;
}

/**
 * Pure helper: the set of co-viewing STUDENT identities among a list of remote
 * participants — everyone whose identity is not the kiosk's `device:` prefix.
 * Admin/staff monitors join as non-publishing participants
 * (`permissions.canPublish === false`) and are excluded too, so the co-viewer
 * strip shows only real classmates. Deduped + stable order. The local
 * participant is never included (it is not a remote participant). Exported for
 * unit testing.
 */
export function studentViewerIdentities(
  participants: Iterable<{
    identity: string;
    permissions?: { canPublish?: boolean };
  }>,
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const p of participants) {
    const id = p.identity;
    if (!id || id.startsWith(DEVICE_IDENTITY_PREFIX)) continue;
    // Admin/staff monitors subscribe but cannot publish — not co-viewers.
    if (p.permissions?.canPublish === false) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/** The device participant's LiveKit identity is prefixed with this. */
export const DEVICE_IDENTITY_PREFIX = "device:";

/** Fallback dropdown label when an operator left the camera name empty. */
export const DEFAULT_CAMERA_LABEL = "Camera";

/** A switchable classroom camera, resolved from a device video publication. */
export interface CameraTrack {
  /** Operator-assigned camera name (or a stable fallback label). */
  name: string;
  /** Underlying track id / sid for dedupe + attach. */
  trackId: string;
}

/**
 * Minimal shape of a remote participant for camera-list extraction. Kept
 * structural so the pure helper is unit-testable without a live Room.
 */
export interface CameraParticipantLike {
  identity: string;
  trackPublications: Map<string, CameraPublicationLike>;
}

export interface CameraPublicationLike {
  kind: Track.Kind | string;
  trackName?: string;
  trackSid?: string;
  isSubscribed?: boolean;
  isMuted?: boolean;
  track?: { sid?: string } | null;
}

function pubTrackId(pub: CameraPublicationLike): string {
  return pub.track?.sid ?? pub.trackSid ?? "";
}

/**
 * Pure helper: build the switchable camera list from a set of remote
 * participants. Only the DEVICE participant's subscribed video publications
 * are returned. Names fall back to a stable label when empty; entries are
 * deduped by trackId (then name) so a re-subscribe doesn't double-list.
 */
export function buildCameraTracks(
  participants: Iterable<CameraParticipantLike>,
): CameraTrack[] {
  const out: CameraTrack[] = [];
  const seen = new Set<string>();
  for (const participant of participants) {
    if (!participant.identity.startsWith(DEVICE_IDENTITY_PREFIX)) continue;
    for (const pub of participant.trackPublications.values()) {
      if (pub.kind !== Track.Kind.Video) continue;
      if (pub.isSubscribed === false) continue;
      // A muted device camera == temporarily hidden by the teacher: drop it
      // from the switcher entirely (parity with a permanently disabled camera)
      // rather than listing it and rendering black.
      if (pub.isMuted) continue;
      const trackId = pubTrackId(pub);
      const name = pub.trackName && pub.trackName.length > 0
        ? pub.trackName
        : DEFAULT_CAMERA_LABEL;
      const dedupeKey = trackId || `name:${name}`;
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      out.push({ name, trackId });
    }
  }
  return out;
}

/**
 * Reduce the configured ICE servers to a SAFE diagnostic summary.
 *
 * SECURITY: an `RTCIceServer` carries `username` + `credential` — those are the
 * shared TURN secrets and must NEVER be logged or shipped. This returns the
 * scheme+host of each URL only, and additionally strips any `user:pass@`
 * userinfo that might be embedded in a malformed URL. Query strings (e.g.
 * `?transport=tcp`) are dropped too: they carry no diagnostic value we need and
 * are one more place a credential could hide.
 *
 * Pure + never throws (a bad entry is reported as "invalid"). Exported for tests.
 */
export function summariseIceServers(servers?: RTCIceServer[]): {
  count: number;
  targets: string[];
} {
  const targets: string[] = [];
  if (!servers || servers.length === 0) return { count: 0, targets };
  for (const server of servers) {
    const raw = server?.urls;
    const urls = Array.isArray(raw) ? raw : raw ? [raw] : [];
    for (const url of urls) {
      try {
        // `turn:user@host:443?transport=tcp` → scheme + host(:port).
        const text = String(url);
        const colon = text.indexOf(":");
        if (colon < 0) {
          targets.push("invalid");
          continue;
        }
        const scheme = text.slice(0, colon).toLowerCase();
        let rest = text.slice(colon + 1);
        rest = rest.split("?")[0] ?? "";
        // Drop any userinfo before an `@` — never ship credentials.
        const at = rest.lastIndexOf("@");
        if (at >= 0) rest = rest.slice(at + 1);
        targets.push(rest.length > 0 ? `${scheme}:${rest}` : scheme);
      } catch {
        targets.push("invalid");
      }
    }
  }
  return { count: targets.length, targets };
}

export interface SubscriberOptions {
  /**
   * TURN/STUN servers from the backend token response. Threaded into the
   * PeerConnection so the student can relay media through coturn on 443 when the
   * network blocks direct candidates. Undefined → LiveKit defaults.
   */
  iceServers?: RTCIceServer[];
  /**
   * E2EE wiring (Phase 4). When true AND the browser supports insertable
   * streams (`isE2EESupported()`), the subscribing Room is built with an
   * `ExternalE2EEKeyProvider` + e2ee worker. The actual media key is installed
   * later via {@link SubscriberClient.applyMediaKey} (on connect and after a QR
   * scan adds a key to the session store). Absent/false → plaintext receive.
   */
  e2ee?: boolean;
}

export class SubscriberClient {
  readonly room: Room;
  private handlers: SubscriberHandlers;
  private state: ConnectionState = "idle";

  // ── E2EE state (Phase 4) ─────────────────────────────────────────────────
  /** True when E2EE was requested AND the browser supports insertable streams. */
  readonly e2eeSupported: boolean;
  /** The active KeyProvider, or null when E2EE is off/unsupported. */
  private e2eeKeyProvider: ExternalE2EEKeyProvider | null = null;
  /** The e2ee worker thread, or null when E2EE is off/unsupported. */
  private e2eeWorker: Worker | null = null;
  /** True once a media key has been installed + per-frame decryption enabled. */
  private e2eeEnabled = false;

  /**
   * Credential-free summary of the ICE servers this room was built with. Used
   * both for diagnostics and to distinguish "your network blocked the relay"
   * from "we never had a relay" when classifying an ICE failure.
   */
  private readonly iceSummary: { count: number; targets: string[] };

  constructor(handlers: SubscriberHandlers, opts: SubscriberOptions = {}) {
    this.handlers = handlers;
    this.iceSummary = summariseIceServers(opts.iceServers);

    // ── E2EE setup (Phase 4) ────────────────────────────────────────────────
    // Build the KeyProvider + e2ee worker BEFORE the Room so the `encryption`
    // option can be baked into the ctor. Gate on the requested flag AND the
    // browser's insertable-streams capability. The media KEY is installed later
    // (applyMediaKey) once a shared key is in the session store — supports LATE
    // JOIN where the key arrives via a QR scan after connect.
    const e2eeRequested = opts.e2ee === true;
    const supported =
      typeof isE2EESupported === "function" ? isE2EESupported() : false;
    this.e2eeSupported = supported;
    if (e2eeRequested && supported) {
      try {
        this.e2eeWorker = new Worker(
          new URL("livekit-client/e2ee-worker", import.meta.url),
          { type: "module" },
        );
        this.e2eeKeyProvider = new ExternalE2EEKeyProvider({
          ratchetSalt: E2EE_KEY_PROVIDER_OPTS.ratchetSalt,
          keySize: E2EE_KEY_PROVIDER_OPTS.keySize,
        });
      } catch (e) {
        console.error("E2EE provider/worker init failed — plaintext receive", e);
        this.e2eeKeyProvider = null;
        this.e2eeWorker = null;
      }
    }

    this.room = new Room({
      ...(this.e2eeKeyProvider && this.e2eeWorker
        ? {
            encryption: {
              keyProvider: this.e2eeKeyProvider,
              worker: this.e2eeWorker,
            },
          }
        : {}),
      // Keep the classroom video always-on. adaptiveStream pauses the
      // subscription on (mis-detected) invisibility and the stream blacks out
      // after a few seconds with no resume — the child only ever needs this
      // one fullscreen video, so adaptiveStream offers nothing here.
      adaptiveStream: false,
      dynacast: false,
      ...(opts.iceServers && opts.iceServers.length > 0
        ? { rtcConfig: { iceServers: opts.iceServers } }
        : {}),
    });

    this.room.on(RoomEvent.Connected, () => this.setState("connected"));
    this.room.on(RoomEvent.Disconnected, (reason?: unknown) => {
      // Diagnostics FIRST but fully isolated: a logging fault must never stop
      // the UI from learning it is disconnected.
      this.logConnectFailure("livekit disconnected", {
        reason: reason === undefined ? "undefined" : String(reason),
        iceFailure: isIceFailureDisconnectReason(reason),
      });
      this.setState("disconnected");
    });
    this.room.on(RoomEvent.Reconnecting, () => this.setState("connecting"));
    // Media-path transitions. Only FAILURE transitions are shipped (the happy
    // path would be pure noise and would eat the failure rate budget).
    this.room.on(RoomEvent.ConnectionStateChanged, (state: unknown) => {
      const s = String(state).toLowerCase();
      if (s === "failed" || s === "disconnected") {
        this.logConnectFailure("livekit connectionState", { state: s });
      }
    });
    this.room.on(RoomEvent.MediaDevicesError, (err: unknown) => {
      this.logConnectFailure("media devices error", {
        detail: err instanceof Error ? err.message : String(err),
      });
    });
    this.room.on(RoomEvent.Reconnected, () => {
      // Re-attach all currently-subscribed remote tracks: a LiveKit reconnect
      // can leave the media elements bound to dead tracks (black video).
      this.reattachSubscribedTracks();
      this.setState("connected");
    });
    this.room.on(
      RoomEvent.TrackSubscribed,
      (
        track: RemoteTrack,
        publication: RemoteTrackPublication,
        participant: RemoteParticipant,
      ) => {
        if (
          track.kind === Track.Kind.Video ||
          track.kind === Track.Kind.Audio
        ) {
          // Invoked on EVERY subscribe (including re-subscribe after an
          // adaptiveStream pause) so the element is re-bound to the live track.
          this.handlers.onTrack(track, publication, participant);
        }
        if (
          track.kind === Track.Kind.Video &&
          participant.identity.startsWith(DEVICE_IDENTITY_PREFIX)
        ) {
          this.handlers.onCameraTracksChanged?.();
        }
      },
    );
    this.room.on(
      RoomEvent.TrackUnsubscribed,
      (
        track: RemoteTrack,
        _publication: RemoteTrackPublication,
        participant: RemoteParticipant,
      ) => {
        if (
          track.kind === Track.Kind.Video ||
          track.kind === Track.Kind.Audio
        ) {
          this.handlers.onTrackUnsubscribed?.(track);
        }
        if (
          track.kind === Track.Kind.Video &&
          participant.identity.startsWith(DEVICE_IDENTITY_PREFIX)
        ) {
          this.handlers.onCameraTracksChanged?.();
        }
      },
    );
    // A muted device camera is the teacher's "temporarily hide" — the track
    // stays published/subscribed but goes black. Treat mute as removal from
    // the switcher (parity with a permanently disabled camera): unbind it and
    // recompute the dropdown. Unmute re-attaches and re-lists it.
    this.room.on(
      RoomEvent.TrackMuted,
      (pub: TrackPublication, participant: Participant) => {
        if (
          pub.kind === Track.Kind.Video &&
          participant.identity.startsWith(DEVICE_IDENTITY_PREFIX)
        ) {
          const track = pub.track as RemoteTrack | undefined;
          if (track) this.handlers.onTrackUnsubscribed?.(track);
          this.handlers.onCameraTracksChanged?.();
        }
      },
    );
    this.room.on(
      RoomEvent.TrackUnmuted,
      (pub: TrackPublication, participant: Participant) => {
        if (
          pub.kind === Track.Kind.Video &&
          participant.identity.startsWith(DEVICE_IDENTITY_PREFIX)
        ) {
          const track = pub.track as RemoteTrack | undefined;
          if (track) {
            this.handlers.onTrack(
              track,
              pub as RemoteTrackPublication,
              participant as RemoteParticipant,
            );
          }
          this.handlers.onCameraTracksChanged?.();
        }
      },
    );
    this.room.on(RoomEvent.DataReceived, (payload: Uint8Array) => {
      const env = decodeIncoming(payload);
      if (env && this.handlers.onData) this.handlers.onData(env);
    });

    // Surface e2ee worker decryption/encryption failures to the UI. The worker
    // emits this when it cannot decrypt a frame (typically a missing key at
    // index 0 before applyMediaKey installs the device key). The UI uses it to
    // show a "key missing / repair" state rather than waiting on the silent
    // late-key retry.
    this.room.on(RoomEvent.EncryptionError, (err: unknown) => {
      this.handlers.onEncryptionError?.(err);
    });

    // Co-viewer presence: keep the viewer-avatar strip in sync as other
    // students join/leave. Recompute the non-device identity set on every
    // participant change (and on reconnect, where the roster is re-synced).
    // Device (publisher) presence drives the paused/Wait overlay backstop.
    const notifyViewers = () => this.notifyViewers();
    this.room.on(
      RoomEvent.ParticipantConnected,
      (participant: RemoteParticipant) => {
        if (participant.identity.startsWith(DEVICE_IDENTITY_PREFIX)) {
          this.handlers.onDevicePresence?.(true);
        }
        notifyViewers();
      },
    );
    this.room.on(
      RoomEvent.ParticipantDisconnected,
      (participant: RemoteParticipant) => {
        // Publisher gone: clear any stale paused/Wait state. Covers the teacher
        // kiosk restarting while paused (it republishes but never sends resume).
        if (participant.identity.startsWith(DEVICE_IDENTITY_PREFIX)) {
          this.handlers.onDevicePresence?.(false);
        }
        notifyViewers();
      },
    );
    this.room.on(RoomEvent.Connected, notifyViewers);
    this.room.on(RoomEvent.Reconnected, notifyViewers);
  }

  /**
   * Ship a connect/media FAILURE breadcrumb to the backend so a field failure is
   * debuggable without asking the family to reproduce it with debug logging on.
   *
   * TOTALLY BEST-EFFORT: wrapped so no diagnostics fault can ever propagate into
   * the connect path or a LiveKit event handler. Only the credential-free ICE
   * summary is attached (see {@link summariseIceServers}) — TURN
   * username/credential must never leave the client.
   */
  private logConnectFailure(msg: string, extra: Record<string, unknown>): void {
    try {
      logDiagFailure(msg, {
        ...extra,
        iceServers: this.iceSummary.count,
        iceTargets: this.iceSummary.targets,
        e2ee: this.e2eeEnabled,
      });
    } catch {
      /* diagnostics must never break the connect path */
    }
  }

  /** Emit the current co-viewing student identities to the UI handler. */
  private notifyViewers(): void {
    this.handlers.onViewersChanged?.(this.getViewerIdentities());
  }

  /** The current co-viewing student identities (non-device remote participants). */
  getViewerIdentities(): string[] {
    return studentViewerIdentities(this.room.remoteParticipants.values());
  }

  private setState(s: ConnectionState) {
    this.state = s;
    this.handlers.onState(s);
  }

  /**
   * Re-bind every currently-subscribed remote video/audio track to its UI
   * element. Idempotent: re-attaching a live track to the same element is a
   * no-op in livekit-client, so this is safe to call repeatedly (e.g. on every
   * Reconnected).
   */
  private reattachSubscribedTracks(): void {
    for (const participant of this.room.remoteParticipants.values()) {
      for (const pub of participant.trackPublications.values()) {
        const track = pub.track;
        if (
          track &&
          (track.kind === Track.Kind.Video || track.kind === Track.Kind.Audio)
        ) {
          this.handlers.onTrack(
            track,
            pub as RemoteTrackPublication,
            participant,
          );
        }
      }
    }
  }

  getState(): ConnectionState {
    return this.state;
  }

  /**
   * Enumerate the DEVICE participant's currently-subscribed video publications
   * as switchable classroom cameras. Other students' cameras are excluded.
   */
  getCameraTracks(): CameraTrack[] {
    return buildCameraTracks(
      this.room.remoteParticipants.values() as Iterable<CameraParticipantLike>,
    );
  }

  /**
   * Resolve the live RemoteTrack for a device camera by its publication
   * trackName (the operator-assigned camera name), so the UI can attach the
   * selected camera. Returns null if no such subscribed video track exists.
   */
  getCameraTrackByName(name: string): RemoteTrack | null {
    for (const participant of this.room.remoteParticipants.values()) {
      if (!participant.identity.startsWith(DEVICE_IDENTITY_PREFIX)) continue;
      for (const pub of participant.trackPublications.values()) {
        if (pub.kind !== Track.Kind.Video) continue;
        const track = pub.track as RemoteTrack | undefined;
        if (!track) continue;
        const label =
          pub.trackName && pub.trackName.length > 0
            ? pub.trackName
            : DEFAULT_CAMERA_LABEL;
        if (label === name) return track;
      }
    }
    return null;
  }

  async connect(url: string, token: string): Promise<void> {
    this.setState("connecting");
    try {
      // Subscriber-only: do not auto-publish anything.
      await this.room.connect(url, token, { autoSubscribe: true });
      // Best-effort: install the device-scoped media key if it is already in
      // the session store (the common case — provisioned at login or scanned
      // before joining). A LATE JOIN where the key is not yet present is handled
      // by Class.svelte calling applyMediaKey() after a successful QR scan.
      if (this.e2eeKeyProvider) {
        await this.applyMediaKey(DEVICE_KEY_SCOPE, DEVICE_KEY_SCOPE_ID);
      }
    } catch (err) {
      this.setState("error");
      // Classify BEFORE handing the failure to the UI so the caller renders a
      // specific, actionable message instead of LiveKit's internal string
      // ("could not establish pc connection"). `iceServerCount` lets the
      // classifier tell "network blocked the relay" from "no relay at all".
      let info: ConnectErrorInfo | undefined;
      try {
        info = classifyConnectError(err, {
          online:
            typeof navigator === "undefined" ? undefined : navigator.onLine,
          iceServerCount: this.iceSummary.count,
        });
        this.logConnectFailure("livekit connect failed", {
          code: info.code,
          category: info.category,
          detail: info.detail,
        });
      } catch {
        /* classification/diagnostics must never mask the original failure */
      }
      this.handlers.onError?.(err, info);
      throw err;
    }
  }

  /**
   * Install a shared media key from the session store into the KeyProvider and
   * turn on per-frame DECRYPTION. Looks the key up by `<scope>:<scopeId>` (the
   * same key `e2eeSession.getMediaKeys()` uses). Idempotent + best-effort:
   *
   *  - no KeyProvider (E2EE off/unsupported) → no-op, returns false.
   *  - no key in the store yet (LATE JOIN before scan) → no-op, returns false;
   *    Class.svelte re-invokes this after a QR scan adds the key.
   *  - key present → setKey + setE2EEEnabled(true), returns true.
   *
   * Returns whether per-frame decryption is enabled after the call.
   */
  async applyMediaKey(scope: string, scopeId: number): Promise<boolean> {
    if (!this.e2eeKeyProvider) return false;
    const key = getMediaKeys().get(`${scope}:${scopeId}`);
    if (!key) return false;
    try {
      await this.e2eeKeyProvider.setKey(mediaKeyToArrayBuffer(key));
      await this.room.setE2EEEnabled(true);
      this.e2eeEnabled = true;
      return true;
    } catch (e) {
      console.error("applyMediaKey failed", e);
      return false;
    }
  }

  /** True once a media key was installed + per-frame decryption enabled. */
  isE2EEEnabled(): boolean {
    return this.e2eeEnabled;
  }

  /**
   * Cheap UI check: is a usable DEVICE-scoped media key currently present in the
   * session store? Unlike {@link isE2EEEnabled} (which reflects whether
   * applyMediaKey has run + decryption is on), this just answers "do we hold the
   * key" — so the UI can distinguish a genuine key-missing state (drive repair /
   * QR re-scan) from a transient not-yet-applied one.
   */
  hasMediaKey(): boolean {
    return getMediaKeys().has(`${DEVICE_KEY_SCOPE}:${DEVICE_KEY_SCOPE_ID}`);
  }

  /**
   * Runtime E2EE toggle (Phase 5 opt-out control). Turns per-frame
   * encryption/decryption on or off on the LIVE room without a reconnect.
   *
   * SYMMETRIC-AWARENESS NOTE: E2EE is whole-session and symmetric. Toggling a
   * student to PLAINTEXT only yields watchable media when the DEVICE side is
   * ALSO not encrypting (i.e. the device is not forcing AND not publishing
   * encrypted). If the device is publishing encrypted frames and the student
   * disables E2EE here, the student's decryption is off and the media will be
   * unwatchable garbage. This mismatch is ACCEPTABLE because the join-time
   * policy already negotiates the symmetric session state server-side (a forced
   * device blocks/locks opt-out); this runtime toggle is only meaningful in the
   * non-forced, device-not-encrypting case. The caller MUST keep the toggle
   * disabled when the negotiated token state is `forced`.
   *
   *  - turning ON requires a media key in the store (applyMediaKey path);
   *    returns whether decryption is enabled afterwards.
   *  - turning OFF disables per-frame crypto on the room and returns false.
   *
   * No-op (returns false) when E2EE is off/unsupported (no KeyProvider).
   */
  async setE2EEEnabled(on: boolean): Promise<boolean> {
    if (!this.e2eeKeyProvider) return false;
    if (on) {
      // Install (if needed) the device-scoped key and enable decryption.
      return this.applyMediaKey(DEVICE_KEY_SCOPE, DEVICE_KEY_SCOPE_ID);
    }
    try {
      await this.room.setE2EEEnabled(false);
      this.e2eeEnabled = false;
      return false;
    } catch (e) {
      console.error("setE2EEEnabled(false) failed", e);
      return this.e2eeEnabled;
    }
  }

  async disconnect(): Promise<void> {
    await this.room.disconnect();
    // Release the e2ee worker thread on teardown.
    try {
      this.e2eeWorker?.terminate();
    } catch {
      /* ignore */
    }
    this.setState("disconnected");
  }

  /** PublishFn compatible with shared/dcwire `publishEnvelope`. */
  publish: PublishFn = (data, reliable) => {
    void this.room.localParticipant.publishData(data as Uint8Array, {
      reliable,
    });
  };

  /** Publish or unpublish the student microphone track. */
  async setMicrophone(on: boolean): Promise<void> {
    await this.room.localParticipant.setMicrophoneEnabled(on);
  }

  /**
   * Publish or unpublish the student camera track. When `deviceId` is given the
   * named webcam is used (the student picked it in the preview dialog);
   * otherwise the browser default camera is used.
   */
  async setCamera(on: boolean, deviceId?: string): Promise<void> {
    await this.room.localParticipant.setCameraEnabled(
      on,
      on && deviceId ? { deviceId } : undefined,
    );
  }
}
